import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * セッション管理 hook (admin 第 16 弾、Phase 5)。
 *
 * 旧 `old/apps/admin/src/pages/SessionsPage.tsx` (280 行) の load / create / archive / unarchive を
 * minimum scope で踏襲。subscribe + create + archive (archivedAt 設定) + unarchive (archivedAt 解除)
 * を提供。
 *
 * 認可: firestore.rules `isDistrictAdmin(districtId)` → master / district admin (担当地区) のみ
 * write 可能。物理 delete は rules で禁止 (履歴・Post.sessionId 維持のため archivedAt 論理削除のみ)。
 *
 * 旧との差分:
 *   - 旧 getDocs (one-shot 取得 + load 関数で手動 reload) → onSnapshot (subscribe で reactive)
 *   - 旧 alert/confirm 直書き UI 副作用 → page 側に分離 (hook は pure CRUD のみ)
 *   - schemaVersion + updatedAt / updatedBy を追加 (DocCommon に合わせる)
 */

/** 一覧表示用 narrow 型 (createdAt 等は admin 表示に最低限のみ保持)。 */
export interface SessionListItem {
  id: string;
  name: string;
  code: string;
  startAt: Date | null;
  endAt: Date | null;
  archivedAt: Date | null;
  postCount: number;
  surveyUrl: string | null;
  createdAt: Date | null;
  /** 表示順 (小さいほど先頭、未設定は startAt 降順フォールバック)。 */
  sortOrder?: number;
}

export type SessionsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: SessionListItem[]; error: null }
  | { status: 'error'; items: []; error: Error };

function tsToDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function mapDoc(snap: { id: string; data: () => DocumentData }): SessionListItem {
  const data = snap.data();
  return {
    id: snap.id,
    name: typeof data.name === 'string' ? data.name : '',
    code: typeof data.code === 'string' ? data.code : '',
    startAt: tsToDate(data.startAt),
    endAt: tsToDate(data.endAt),
    archivedAt: tsToDate(data.archivedAt),
    postCount: typeof data.postCount === 'number' ? data.postCount : 0,
    surveyUrl: typeof data.surveyUrl === 'string' ? data.surveyUrl : null,
    createdAt: tsToDate(data.createdAt),
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : undefined,
  };
}

/**
 * セッション表示順の pure ソート (admin / user 共通仕様)。
 *
 * 仕様: `sortOrder` 昇順 (小さいほど先頭、未設定は末尾) → 同値 / 双方未設定は startAt 降順
 * (新しいものが先頭、startAt=null は最後)。Firestore の orderBy は未設定 doc を結果から
 * 除外するため、並べ替えは取得後に client 側で本関数を通す。元配列は破壊しない。
 */
export function sortSessions<T extends { sortOrder?: number; startAt: Date | null }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.POSITIVE_INFINITY;
    const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    // 同 sortOrder (双方未設定含む) は startAt 降順。null は最後尾へ。
    const at = a.startAt ? a.startAt.getTime() : Number.NEGATIVE_INFINITY;
    const bt = b.startAt ? b.startAt.getTime() : Number.NEGATIVE_INFINITY;
    return bt - at;
  });
}

export function useSessions(districtId: string | null): SessionsState {
  const [state, setState] = useState<SessionsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'sessions'),
      orderBy('startAt', 'desc'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        // orderBy('startAt') は維持しつつ、最終並びは client 側で sortOrder 昇順 → startAt 降順。
        const items = sortSessions(snap.docs.map(mapDoc));
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;

/**
 * 6 桁セッションコード生成 (旧 `genCode()` 踏襲、1/I/0/O 等の視認性悪い文字を排除済 alphabet)。
 * crypto.getRandomValues 由来の予測不能性 (一意性は呼出側で確保する想定、衝突確率は
 * 32^6 ≈ 10 億分の 1 で実運用では無視)。
 */
export function generateSessionCode(): string {
  const bytes = new Uint8Array(CODE_LENGTH);
  crypto.getRandomValues(bytes);
  let s = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    const byte = bytes[i] ?? 0;
    s += CODE_ALPHABET[byte % CODE_ALPHABET.length];
  }
  return s;
}

export interface CreateSessionInput {
  name: string;
  /** 開始日時 (ISO8601、null = 即時 = now)。 */
  startAt: string | null;
  /** 終了日時 (ISO8601、null = startAt + 30 日)。 */
  endAt: string | null;
  surveyUrl?: string | null;
}

export async function createSession(
  districtId: string,
  input: CreateSessionInput,
  uid: string,
): Promise<{ id: string; code: string }> {
  const id = crypto.randomUUID();
  const code = generateSessionCode();
  const start = input.startAt ? new Date(input.startAt) : new Date();
  const end = input.endAt
    ? new Date(input.endAt)
    : new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
  const ref = doc(db, 'districts', districtId, 'sessions', id);
  await setDoc(ref, {
    schemaVersion: 1,
    name: input.name.trim(),
    code,
    startAt: Timestamp.fromDate(start),
    endAt: Timestamp.fromDate(end),
    surveyUrl: input.surveyUrl ?? null,
    postCount: 0,
    archivedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  });
  return { id, code };
}

/**
 * archivedAt を設定して非表示化 (旧 v1 archive ボタン同 logic、Post.sessionId は維持)。
 */
export async function archiveSession(
  districtId: string,
  sessionId: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'sessions', sessionId);
  await setDoc(
    ref,
    { archivedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: uid },
    { merge: true },
  );
}

/** archivedAt を null 解除して復帰 (旧 v1 unarchive ボタン同 logic)。 */
export async function unarchiveSession(
  districtId: string,
  sessionId: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'sessions', sessionId);
  await setDoc(
    ref,
    { archivedAt: null, updatedAt: serverTimestamp(), updatedBy: uid },
    { merge: true },
  );
}

export interface UpdateSessionPatch {
  name?: string;
  /** 開始日時 (ISO8601)。createSession と同形式で Timestamp に変換して保存。 */
  startAt?: string;
  /** 終了日時 (ISO8601)。 */
  endAt?: string;
  /** 表示順 (小さいほど先頭)。 */
  sortOrder?: number;
}

/**
 * セッションの編集 (name / startAt / endAt / sortOrder)。
 * 渡された field のみ merge write する (undefined は patch に含めない)。
 * startAt / endAt は ISO 文字列を受け取り、createSession と同じく Timestamp で保存。
 */
export async function updateSession(
  districtId: string,
  sessionId: string,
  patch: UpdateSessionPatch,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'sessions', sessionId);
  const data: DocumentData = { updatedAt: serverTimestamp(), updatedBy: uid };
  if (patch.name !== undefined) data.name = patch.name.trim();
  if (patch.startAt !== undefined) data.startAt = Timestamp.fromDate(new Date(patch.startAt));
  if (patch.endAt !== undefined) data.endAt = Timestamp.fromDate(new Date(patch.endAt));
  if (patch.sortOrder !== undefined) data.sortOrder = patch.sortOrder;
  await setDoc(ref, data, { merge: true });
}

/**
 * ドラッグ&ドロップ確定時の一括並び替え。
 * 渡された sessionId 配列の順序で sortOrder を index ベース (0,1,2...) に振り直し、
 * writeBatch でまとめて保存 (1 batch = atomic)。アクティブセッションのみを対象とする想定。
 */
export async function reorderSessions(
  districtId: string,
  orderedSessionIds: readonly string[],
  uid: string,
): Promise<void> {
  // Firestore writeBatch は 1 batch 最大 500 write。セッションは現実的に数十件だが念のためガード。
  if (orderedSessionIds.length > 500) {
    throw new Error('並び替え対象のセッションが多すぎます (一度に並び替えできるのは 500 件まで)。');
  }
  const batch = writeBatch(db);
  orderedSessionIds.forEach((sessionId, index) => {
    const ref = doc(db, 'districts', districtId, 'sessions', sessionId);
    batch.set(
      ref,
      { sortOrder: index, updatedAt: serverTimestamp(), updatedBy: uid },
      { merge: true },
    );
  });
  await batch.commit();
}
